import { Loader } from "@googlemaps/js-api-loader";

// --- Configuration ---
const API_KEY = window.ajaxParams?.googleApiKey || 'YOUR_FALLBACK_API_KEY'; // Use fallback or ensure it's set
const THEME_BASE_URL = window.ajaxParams?.themebase || '/wp-content/themes/your-theme';
const MAP_ID = window.ajaxParams?.mapId || '8e0a97af9386fef'; // Default or from ajaxParams
const MAP_ICON = `${THEME_BASE_URL}/src/assets/images/map-icon-active.png`;

// Function to get AJAX URL (remains the same)
const getAjaxUrl = () => {
  if (window.ajaxParams?.ajaxurl) {
    return window.ajaxParams.ajaxurl;
  }
  if (window.location.origin) {
    return `${window.location.origin}/wp-admin/admin-ajax.php`;
  }
  return '/wp-admin/admin-ajax.php';
};
const AJAX_URL = getAjaxUrl();

// MAP_CONFIG remains largely the same, but styles are ignored when mapId is used
const MAP_CONFIG = {
  coordinates: {
    lat: parseFloat('37.0744408'),
    lng: parseFloat('-98.0267338'),
  },
  zoom: 4.2,
  mapId: MAP_ID, // Cloud-based Map ID
  // Styles array below is ignored when mapId is specified in mapOptions
  styles: [
    { elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
    { featureType: 'landscape.natural', elementType: 'geometry.fill', stylers: [{ color: '#f5f5f2' }, { visibility: 'on' }] },
    { featureType: 'administrative', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.province', stylers: [{ visibility: 'on' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
    { featureType: 'landscape.man_made', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }, { visibility: 'on' }] },
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.medical', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.sports_complex', stylers: [{ visibility: 'off' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#ffffff' }, { visibility: 'simplified' }] },
    { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ffffff' }, { visibility: 'simplified' }] },
    { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#dfecec' }] },
  ],
};

// --- Map Class ---
class ExpansionMap {
  constructor(mapElement, infoElement, refreshButtonElement) {
    this.mapElement = mapElement;
    this.infoElement = infoElement;
    this.refreshButtonElement = refreshButtonElement;
    this.pageIdentifier = mapElement?.dataset.page || 'default_page';
    this.ajaxUrl = AJAX_URL;
    this.markers = [];
    this.cachedLocations = null; // Store cached locations array

    // API components will be stored here after loading
    this.Map = null;
    this.LatLng = null;
    this.Point = null;
    this.Size = null;
    this.AdvancedMarkerElement = null; // Renamed from Marker to AdvancedMarkerElement
    this.ControlPosition = null;
    this.Geocoder = null; // For geocoding

    this.map = null; // The map instance
    this.geocoder = null; // The geocoder instance

    if (!this.mapElement || !this.infoElement) {
      console.warn('Required map elements (mapElement, infoElement) not found. Initialization aborted.');
      // Consider throwing an error or providing visual feedback on the page
      return;
    }

    if (!this.refreshButtonElement) {
      // Refresh button not available (user may not be logged in)
    }
  }

  verifyAjaxUrl() {
    try {
      const testUrl = new URL(this.ajaxUrl, window.location.origin);
    } catch (err) {
      console.error('Invalid AJAX URL configuration:', this.ajaxUrl, err);
    }
  }

  async initialize() {
    // Check for API Key *before* attempting to load
    if (!API_KEY || API_KEY === 'YOUR_FALLBACK_API_KEY') {
      console.error("Google Maps API Key is missing or invalid! Please configure it in window.ajaxParams.googleApiKey or directly.");
      this.mapElement.innerHTML = '<p style="color:red;text-align:center;">Map Error: Missing API Key.</p>';
      return; // Stop initialization
    }

    try {
      const loader = new Loader({
        apiKey: API_KEY,
        version: "weekly",
        // Include libraries needed. 'maps' includes core + Geocoder. 'marker' for Advanced Markers.
        libraries: ["maps", "marker"],
      });

      // Await loading of libraries. These calls ensure the global 'google.maps' is populated.
      await loader.importLibrary('maps');

      // Import the marker library and specifically import AdvancedMarkerElement
      const { AdvancedMarkerElement } = await loader.importLibrary('marker');

      // Destructure *all* required components from the global google.maps namespace AFTER loading
      const { Map, LatLng, Point, Size, ControlPosition, Geocoder } = window.google.maps;

      // Store constructors/classes on the instance for consistent access
      this.Map = Map;
      this.LatLng = LatLng;
      this.Point = Point;
      this.Size = Size;
      this.AdvancedMarkerElement = AdvancedMarkerElement; // Store AdvancedMarkerElement
      this.ControlPosition = ControlPosition;
      this.Geocoder = Geocoder; // Store Geocoder class

      const mapOptions = {
        center: MAP_CONFIG.coordinates,
        zoom: MAP_CONFIG.zoom,
        mapId: MAP_CONFIG.mapId, // Use the Cloud-based Map ID
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControlOptions: {
          position: this.ControlPosition.LEFT_BOTTOM,
        },
        gestureHandling: 'cooperative',
      };

      // Create the map instance using the stored constructor
      this.map = new this.Map(this.mapElement, mapOptions);

      // Create the geocoder instance using the stored constructor
      this.geocoder = new this.Geocoder();

      this.setupEventListeners();
      await this.fetchLocations(); // Fetch initial data
    } catch (err) {
      console.error('Failed to initialize map or load Google Maps API:', err);
      this.mapElement.innerHTML =
        '<p style="color:red;text-align:center;">Error loading map. Check API Key, network, and console for details.</p>';
    }
  }

  async testAjax() {
    // Ensure jQuery is available
    if (typeof jQuery === 'undefined') {
      console.error("jQuery is required for testAjax.");
      return Promise.reject(new Error("jQuery not found"));
    }
    const nonce = window.ajaxParams?.nonce; // Check nonce
    return new Promise((resolve, reject) => {
      jQuery.ajax({
        url: this.ajaxUrl,
        method: 'POST',
        dataType: 'json',
        data: { action: 'test_ajax', nonce: nonce || '' }, // Send nonce if available
        success: (response) => {
          // AJAX Test Success
          resolve(response);
        },
        error: (xhr, textStatus, errorThrown) => {
          console.error('AJAX test error:', textStatus, errorThrown);
          console.error('Response payload:', xhr.responseText);
          reject(new Error(`AJAX Test Failed: ${textStatus}: ${errorThrown}`));
        }
      });
    });
  }

  setupEventListeners() {
    // Use optional chaining for safety
    if (this.refreshButtonElement) {
      this.refreshButtonElement.addEventListener('click', () => this.handleRefresh());
    }
  }

  async handleRefresh() {
    const btn = this.refreshButtonElement;
    if (!btn) return;

    const original = btn.textContent;
    btn.textContent = 'Refreshing…';
    btn.disabled = true;

    try {
      const locations = await this.fetchData('action_refresh_locations');
      // plotLocations is async, so await it
      await this.plotLocations(locations);
    } catch (err) {
      console.error('Refresh failed:', err);
      this.infoElement.innerHTML = '<p style="color:red;">Failed to refresh locations.</p>';
    } finally {
      btn.textContent = original;
      btn.disabled = false;
    }
  }

  async fetchData(action) {
    if (typeof jQuery === 'undefined') {
      console.error("jQuery is required for fetchData.");
      return Promise.reject(new Error("jQuery not found"));
    }
    const nonce = window.ajaxParams?.nonce;
    if (!nonce) {
      console.warn(`Nonce not found for action: ${action}. AJAX call might fail if nonce is required.`);
    }

    return new Promise((resolve, reject) => {
      jQuery.ajax({
        url: this.ajaxUrl,
        method: 'POST',
        dataType: 'json',
        data: {
          action,
          nonce: nonce || '', // Send nonce or empty string
          page_id: this.pageIdentifier,
        },
        success: (response) => {
          // Standard WordPress AJAX response check
          if (typeof response === 'object' && response !== null && typeof response.success === 'boolean') {
            if (!response.success) {
              const errorMessage = response.data?.message || response.data || 'Unknown AJAX error (success: false)';
              console.error(`AJAX error for action ${action}:`, errorMessage);
              reject(new Error(errorMessage));
            } else {
              resolve(response.data); // Resolve with the actual data payload
            }
          } else {
            console.warn(`Unexpected AJAX response format for action ${action}:`, response);
            reject(new Error("Invalid AJAX response format")); // Reject on unexpected format
          }
        },
        error: (xhr, textStatus, errorThrown) => {
          console.error(`AJAX fetch error (${action}):`, textStatus, errorThrown);
          console.error('Response payload:', xhr.responseText);
          reject(new Error(`AJAX Error: ${textStatus} - ${errorThrown || 'Server error'}`));
        }
      });
    });
  }

  async fetchLocations() {
    try {
      const locations = await this.fetchData('action_fetch_locations');
      // plotLocations is async, so await it
      await this.plotLocations(locations);
    } catch (err) {
      console.error('Initial fetch locations failed:', err);
      this.infoElement.innerHTML = '<p style="color:red;">Failed to load locations.</p>';
    }
  }

  clearMarkers() {
    this.markers.forEach((marker) => {
      // For AdvancedMarkerElement, use marker.map = null
      marker.map = null;
    });
    this.markers = [];
    this.infoElement.innerHTML = '<p>Click on a city to learn more!</p>'; // Show default text when clearing markers
  }

  async plotLocations(locations) {
    this.clearMarkers(); // Clear existing markers first

    if (!Array.isArray(locations) || locations.length === 0) {
      console.warn('No locations provided to plot.');
      this.infoElement.innerHTML = '<p>No locations found. Please check back later!</p>';
      this.recenterMap(); // Recenter even if no markers
      return;
    }

    // Load all cached locations at once before processing
    try {
      await this.getAllCachedLocations();
    } catch (error) {
      console.warn('Failed to load cached locations, proceeding with geocoding:', error);
      this.cachedLocations = [];
    }
    let plottedCount = 0;

    // Use for...of loop to correctly handle await inside the loop
    for (const loc of locations) {
      try {
        // Await the creation (which might involve async geocoding)
        const markerCreated = await this.createLocationMarker(loc);
        if (markerCreated) {
          plottedCount++;
        }
      } catch (error) {
        console.error("Error creating marker for location:", loc, error);
      }
    }

    // Markers plotted successfully

    // Optional: Fit bounds after all markers are potentially plotted
    if (this.markers.length > 0) {
      this.fitBoundsToMarkers();
    } else {
      this.recenterMap(); // Recenter if no markers ended up being plotted
    }
  }

    async geocodeLocation(location) {
    if (!this.geocoder) {
      console.error('Geocoder not initialized.');
      return null;
    }

    const addressQuery = location.address || location.city || '';

    if (!addressQuery) {
      console.warn('Cannot geocode location, missing address/city field:', location);
      return null;
    }

    // First, try to get cached coordinates from our loaded cache
    const cachedCoords = this.getCachedCoordinatesForAddress(addressQuery);
    if (cachedCoords && cachedCoords.lat && cachedCoords.lng) {
      return cachedCoords;
    }

    try {
      const response = await this.geocoder.geocode({ address: addressQuery });
      if (response.results && response.results.length > 0) {
        const geometry = response.results[0].geometry;
        if (geometry && geometry.location) {
          const lat = geometry.location.lat();
          const lng = geometry.location.lng();
          const coordinates = { lat, lng };

          // Add to our local cache and update the server cache
          try {
            await this.addLocationToCache(addressQuery, coordinates);
          } catch (error) {
            console.warn(`Failed to cache coordinates for "${addressQuery}":`, error);
            // Don't fail the whole process if caching fails
          }

          return coordinates;
        }
      }
      console.warn(`❌ Geocoding failed for "${addressQuery}". Status: ${response.status || 'ZERO_RESULTS'}`);
      return null;
    } catch (error) {
      console.error(`❌ Geocoding error for "${addressQuery}":`, error);
      return null;
    }
  }

  async addLocationToCache(addressQuery, coordinates) {
    if (!this.cachedLocations) {
      this.cachedLocations = [];
    }

    const locationSlug = this.generateLocationSlug(addressQuery);

    // Check if location already exists in cache
    const existingIndex = this.cachedLocations.findIndex(loc => loc.slug === locationSlug);

    const newLocation = {
      slug: locationSlug,
      address: addressQuery,
      lat: coordinates.lat,
      lng: coordinates.lng,
      cached_at: new Date().toISOString(),
    };

    if (existingIndex >= 0) {
      // Update existing location
      this.cachedLocations[existingIndex] = newLocation;
    } else {
      // Add new location
      this.cachedLocations.push(newLocation);
    }

    // Update the server cache
    await this.updateCachedLocations(this.cachedLocations);
  }

    async getAllCachedLocations() {
    if (typeof jQuery === 'undefined') {
      console.error("jQuery is required for getAllCachedLocations.");
      return Promise.reject(new Error("jQuery not found"));
    }

    const nonce = window.ajaxParams?.nonce;

    return new Promise((resolve, reject) => {
      jQuery.ajax({
        url: this.ajaxUrl,
        method: 'POST',
        dataType: 'json',
        data: {
          action: 'get_all_cached_locations',
          nonce: nonce || '',
        },
        success: (response) => {
          if (typeof response === 'object' && response !== null && typeof response.success === 'boolean') {
            if (response.success) {
              // Store cached locations for future reference
              this.cachedLocations = response.data || [];
              resolve(this.cachedLocations);
            } else {
              // No cached locations found, return empty array
              this.cachedLocations = [];
              resolve(this.cachedLocations);
            }
          } else {
            console.warn(`Unexpected response format for cached locations:`, response);
            this.cachedLocations = [];
            resolve(this.cachedLocations);
          }
        },
        error: (xhr, textStatus, errorThrown) => {
          console.error(`Error getting cached locations:`, textStatus, errorThrown);
          reject(new Error(`AJAX Error: ${textStatus} - ${errorThrown || 'Server error'}`));
        }
      });
    });
  }

  async updateCachedLocations(newLocations) {
    if (typeof jQuery === 'undefined') {
      console.error("jQuery is required for updateCachedLocations.");
      return Promise.reject(new Error("jQuery not found"));
    }

    const nonce = window.ajaxParams?.nonce;

    return new Promise((resolve, reject) => {
      jQuery.ajax({
        url: this.ajaxUrl,
        method: 'POST',
        dataType: 'json',
        data: {
          action: 'update_cached_locations',
          nonce: nonce || '',
          locations: JSON.stringify(newLocations),
        },
        success: (response) => {
          if (typeof response === 'object' && response !== null && typeof response.success === 'boolean') {
            if (response.success) {
              // Update our local cache
              this.cachedLocations = newLocations;
              resolve(response.data);
            } else {
              const errorMessage = response.data?.message || response.data || 'Failed to update cached locations';
              console.error(`Error updating cached locations:`, errorMessage);
              reject(new Error(errorMessage));
            }
          } else {
            console.warn(`Unexpected response format for location caching:`, response);
            reject(new Error("Invalid response format"));
          }
        },
        error: (xhr, textStatus, errorThrown) => {
          console.error(`Error updating cached locations:`, textStatus, errorThrown);
          reject(new Error(`AJAX Error: ${textStatus} - ${errorThrown || 'Server error'}`));
        }
      });
    });
  }

  getCachedCoordinatesForAddress(addressQuery) {
    if (!this.cachedLocations || !Array.isArray(this.cachedLocations)) {
      return null;
    }

    // Generate slug from address for comparison
    const locationSlug = this.generateLocationSlug(addressQuery);

    // Find matching cached location
    const cachedLocation = this.cachedLocations.find(loc => loc.slug === locationSlug);

    if (cachedLocation && cachedLocation.lat && cachedLocation.lng) {
      return { lat: cachedLocation.lat, lng: cachedLocation.lng };
    }

    return null;
  }

  generateLocationSlug(address) {
    // Mimic WordPress sanitize_title function
    return address
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '-')          // Replace spaces with hyphens
      .replace(/-+/g, '-')           // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, '');        // Remove leading/trailing hyphens
  }

  async createLocationMarker(location) {
    // Ensure necessary API components are loaded and stored
    if (!(this.LatLng && this.AdvancedMarkerElement && this.Point && this.Size)) {
      console.error("Cannot create marker: Maps API components not fully loaded.");
      return false;
    }

    let lat = location.lat ? parseFloat(location.lat) : NaN;
    let lng = location.lng ? parseFloat(location.lng) : NaN;

    // Attempt geocoding if coordinates are missing/invalid
    if (isNaN(lat) || isNaN(lng)) {
      const addressQuery = location.address || location.city || '';
      if (!addressQuery) {
        console.error(`Cannot geocode location - missing address/city field:`, location);
        return false; // Cannot geocode without address or city
      }
      // No coordinates in location data, looking up coordinates
      const coordinates = await this.geocodeLocation(location);
              if (coordinates && typeof coordinates.lat === 'number' && typeof coordinates.lng === 'number') {
          lat = coordinates.lat;
          lng = coordinates.lng;
        } else {
        console.error(`✗ Geocoding failed for: "${addressQuery}". Skipping marker.`);
        return false; // Indicate marker not created
      }
    }

    // Final check on coordinates
    if (isNaN(lat) || isNaN(lng)) {
      const addressQuery = location.address || location.city || 'Unknown location';
      console.error(`Final coordinates invalid for: "${addressQuery}". Skipping marker.`);
      return false; // Indicate marker not created
    }

    // Create position for the marker
    const position = new this.LatLng(lat, lng);

    // Create content for the marker (icon + label)
    const markerIcon = document.createElement('img');
    markerIcon.src = MAP_ICON;
    markerIcon.style.width = '30px';
    markerIcon.style.height = '33px';
    markerIcon.setAttribute('data-marker-type', 'default'); // Add attribute for easier state tracking

    // Create label element separately if needed, or style a container
    const markerLabel = document.createElement('div');
    markerLabel.textContent = location.city || '';
    markerLabel.style.color = '#41505f';
    markerLabel.style.fontWeight = 'bold';
    markerLabel.style.fontSize = '11px';
    markerLabel.style.backgroundColor = 'white';
    markerLabel.style.padding = '1px 2px';
    markerLabel.style.borderRadius = '2px';
    markerLabel.style.border = '1px solid #41505f';
    markerLabel.style.boxShadow = '0 0 2px 0 rgba(0, 0, 0, 0.5)';
    markerLabel.style.marginTop = '4px'; // Spacing between icon and label
    markerLabel.style.whiteSpace = 'nowrap'; // Prevent text wrapping

    const markerContent = document.createElement('div');
    markerContent.appendChild(markerIcon);
    markerContent.appendChild(markerLabel);
    markerContent.style.cursor = 'pointer';
    markerContent.style.display = 'flex';
    markerContent.style.flexDirection = 'column';
    markerContent.style.alignItems = 'center';
    markerContent.style.justifyContent = 'center';
    // Position the marker so the anchor point is at the center of the label, 3px above it
    // Much smaller adjustment - just shift up by about half the label height plus small offset
    markerContent.style.transform = 'translateY(20px)';
    markerContent.style.position = 'relative';
    markerContent.setAttribute('data-location-id', location.id || ''); // Store location ID for reference

    // Create the AdvancedMarkerElement
    const marker = new this.AdvancedMarkerElement({
      position,
      map: this.map,
      title: location.title || '',
      content: markerContent, // Use the custom DOM element for content
    });

    // Store the location data with the marker for easy access
    marker.locationData = location;

    // --- Info Window Content --- (remains the same)
    const domainsHtml = Array.isArray(location.domains)
      ? location.domains.map((d) => `<li>${d.domain}</li>`).join('')
      : '';
    const infoContent = `
      <div class="expansion-map__info-window">
        <h4 class="expansion-map__info-window--title">
          <a href="${location.link || '#'}" target="_blank" rel="noopener noreferrer">
            ${location.title || 'Details'}
          </a>
        </h4>
        <div class="expansion-map__info-window--description">
          ${location.description || ''}
        </div>
        ${domainsHtml ? `<ul class="expansion-map__info-window--list">${domainsHtml}</ul>` : ''}
        <a href="${location.link || '#'}" target="_blank" rel="noopener noreferrer"
           class="expansion-map__info-window--link">
           Visit Site
        </a>
      </div>`;

    // --- Event Listener ---
    // AdvancedMarkerElement uses 'click' event, not 'gmp-click'
    marker.addListener('gmp-click', () => {
      this.handleMarkerClick(marker, markerContent, infoContent);
    });

    this.markers.push(marker);
    return true; // Indicate marker was created
  }

  handleMarkerClick(clickedMarker, markerContentElement, infoHtmlContent) {
    // 1. Reset appearance of other markers
    this.resetMarkerIcons();

    // 2. Update appearance of the clicked marker
    // For AdvancedMarkerElement, modify the 'content' element directly
    const iconElement = markerContentElement.querySelector('img');
    if (iconElement) {
      iconElement.src = MAP_ICON;
      iconElement.setAttribute('data-marker-type', 'active');
    }

    // Update label colors for active marker (swap background and text colors)
    const labelElement = markerContentElement.querySelector('div:last-child');
    if (labelElement) {
      labelElement.style.backgroundColor = '#41505f'; // Dark background
      labelElement.style.color = 'white'; // White text
    }

    // Bump z-index to bring selected marker to front
    markerContentElement.style.zIndex = '1000';

    // Add an 'active' class to the markerContentElement
    markerContentElement.classList.add('active-marker');

    // 3. Display info in the panel
    this.infoElement.innerHTML = infoHtmlContent;

    // Optional: Pan map to center the clicked marker smoothly
    if (this.map && clickedMarker.position) {
      this.map.panTo(clickedMarker.position);
    }
  }

  resetMarkerIcons() {
    this.markers.forEach((marker) => {
      // Access the content element of the AdvancedMarkerElement
      const contentElement = marker.content;
      if (contentElement) {
        const iconElement = contentElement.querySelector('img');
        if (iconElement) {
          iconElement.src = MAP_ICON; // Reset icon source
          iconElement.setAttribute('data-marker-type', 'default');
        }

        // Reset label colors to original state
        const labelElement = contentElement.querySelector('div:last-child');
        if (labelElement) {
          labelElement.style.backgroundColor = 'white'; // White background
          labelElement.style.color = '#41505f'; // Dark text
        }

        // Reset z-index to default
        contentElement.style.zIndex = '';

        // Remove the active class if it was added
        contentElement.classList.remove('active-marker');
      }
    });
    // Show default text when no marker is selected
    this.infoElement.innerHTML = '<p>Click on a city to learn more!</p>';
  }

  recenterMap() {
    if (this.map && this.LatLng) { // Check if map and LatLng are ready
      // Use the stored LatLng constructor
      this.map.setCenter(new this.LatLng(MAP_CONFIG.coordinates.lat, MAP_CONFIG.coordinates.lng));
      this.map.setZoom(MAP_CONFIG.zoom);
    } else {
      console.warn("Cannot recenter map - map or LatLng not ready.");
    }
  }

  fitBoundsToMarkers() {
    if (!this.map || this.markers.length === 0) {
      this.recenterMap();
      return;
    }
    // Ensure LatLngBounds is available (part of 'maps' library)
    if (!window.google.maps.LatLngBounds) {
      console.error("LatLngBounds not available for fitBoundsToMarkers.");
      this.recenterMap();
      return;
    }
    const LatLngBounds = window.google.maps.LatLngBounds;

    const bounds = new LatLngBounds();
    this.markers.forEach(marker => {
      // AdvancedMarkerElement has a 'position' property (google.maps.LatLng)
      if (marker.position) {
        bounds.extend(marker.position);
      }
    });

    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds);
      // Add padding if only one marker or bounds are too tight
      if (this.markers.length === 1) {
        this.map.setZoom(Math.min(this.map.getZoom(), 15)); // Avoid excessive zoom
      }
    } else {
      console.warn("Could not fit bounds - no valid marker positions found.");
      this.recenterMap();
    }
  }
}

// --- Initialization --- (Simplified)
function initApp() {
  const mapCanvas = document.querySelector('#expansion-map');
  const mapInfo = document.querySelector('#map-info');
  const refreshButton = document.querySelector('#refresh-map');

  if (mapCanvas && mapInfo && refreshButton) {
    const expansionMap = new ExpansionMap(mapCanvas, mapInfo, refreshButton);
    // The initialize method is async and handles API loading internally
    expansionMap.initialize();
  } else {
    console.warn('Initialization failed: Some required elements not found.');
    // Try to initialize without refresh button if map and info exist
    if (mapCanvas && mapInfo) {
      const expansionMap = new ExpansionMap(mapCanvas, mapInfo, null);
      expansionMap.initialize();
    }
  }
}

// Use DOMContentLoaded to ensure elements are available
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  // DOMContentLoaded has already fired
  initApp();
}

// Make the class available globally if needed by other scripts (optional)
// window.ExpansionMap = ExpansionMap;
